import { inject, Injectable, signal } from '@angular/core';
import { StorageService } from '@app-shared/services/storage/storage.service';
import {
  FundProvidersData,
  FundProvidersService,
  FundProvidersState,
  PreferenceKey,
} from '@app-shared/services/fund-providers/fund-provider.service';
import { withoutRetiredPreferences } from '@app-shared/services/fund-providers/preference-options';
import { NotFoundException } from '@app-shared/services/not-found-exception';
import { RiskCategory } from '@app-shared/services/calculator/riskQuestions';
import * as Sentry from '@sentry/browser';
import { OnboardingData } from '@app-shared/services/onboarding/onboarding.service';
import { FunctionsService } from '@app-shared/services/functions/functions.service';
import {
  Ethics,
  EthicsExposureDecision,
} from '@app-shared/services/profile/profile-ethics.service';

/** Request format for the recommendation_engine_v4 callable (snake_case) */
export type RecommendationRequestV4 = {
  risk_category: RiskCategory;
  selected_preferences: string[];
  top_priorities: string[];
  rewards_preference: boolean;
};

/** Ethics-exposure stances that indicate the member wants to avoid a category. */
const AVOIDANCE_STANCES: ReadonlySet<EthicsExposureDecision> = new Set([
  EthicsExposureDecision.exposureChoice1, // 'def not'
  EthicsExposureDecision.exposureChoice2, // 'not really'
]);

/** Maps each legacy ethics field to its v4 avoidance preference key. */
const ETHICS_TO_PREFERENCE_KEY: readonly [keyof Ethics, PreferenceKey][] = [
  ['adultEntertainment', PreferenceKey.AdultEntertainment],
  ['alcohol', PreferenceKey.Alcohol],
  ['animalTesting', PreferenceKey.AnimalTesting],
  ['civilianWeapons', PreferenceKey.CivilianWeapons],
  ['gambling', PreferenceKey.Gambling],
  ['nuclearPower', PreferenceKey.NuclearPower],
  ['tobacco', PreferenceKey.Tobacco],
  ['weaponsOfWar', PreferenceKey.WeaponsOfWar],
];

/**
 * Translates a legacy v3 {@link Ethics} object into a v4 {@link RecommendationRequestV4}.
 *
 * Each avoidance category where the member expressed a negative stance
 * ('def not' / 'not really') becomes a selected preference; sustainability is
 * added when opted in. `top_priorities` and `rewards_preference` have no v3
 * equivalent, so they default to empty / false. When no avoidance stance is
 * expressed, the request carries no preferences and v4 ranks purely by
 * `bettersaverScore`.
 */
export function ethicsToV4Request(
  riskCategory: RiskCategory,
  ethics: Ethics,
): RecommendationRequestV4 {
  const selectedPreferences: PreferenceKey[] = [];

  for (const [ethicsField, preferenceKey] of ETHICS_TO_PREFERENCE_KEY) {
    if (AVOIDANCE_STANCES.has(ethics[ethicsField] as EthicsExposureDecision)) {
      selectedPreferences.push(preferenceKey);
    }
  }

  if (ethics.sustainability) {
    selectedPreferences.push(PreferenceKey.Sustainability);
  }

  return {
    risk_category: riskCategory,
    selected_preferences: selectedPreferences,
    top_priorities: [],
    rewards_preference: false,
  };
}

/** Response format from the recommendation_engine_v4 callable */
type RecommendationResponseV4 = {
  fundProvidersData: FundProvidersData[];
  weights: Record<string, number>;
  quality_fallback?: boolean;
  rewards_fallback?: boolean;
};

export type FundImages = {
  titleImagePath: string;
  imageGraphPath: string;
  imageFeesPath: string;
};

/**
 * Builds the {@link RecommendationRequestV4} that gets sent to the engine from
 * either an `OnboardingData` record (onboarding flow) or an explicit
 * `RecommendationRequestV4` (admin / CRM preview callers).
 *
 * Kept as a pure module function so both the engine call and the cache
 * signature derive the engine inputs identically — the signature can never
 * drift from what the engine actually receives.
 */
export function toV4Request(
  input: OnboardingData | RecommendationRequestV4,
): RecommendationRequestV4 {
  // Retired preferences (e.g. nz_holdings) are stripped here, the one place
  // every engine request is built, so they never count toward matching
  // whatever the caller or however old the stored onboarding data.
  if ('risk_category' in input) {
    return {
      ...input,
      selected_preferences: withoutRetiredPreferences(input.selected_preferences),
      top_priorities: withoutRetiredPreferences(input.top_priorities),
    };
  }
  return {
    risk_category: input.investment_summary as RiskCategory,
    selected_preferences: withoutRetiredPreferences(input.selected_preferences ?? []),
    top_priorities: withoutRetiredPreferences(input.top_priorities ?? []),
    rewards_preference: input.rewards_preference ?? false,
  };
}

/**
 * Computes a stable, order-normalised signature of the four inputs sent to
 * `recommendation_engine_v4` ({@link RecommendationRequestV4}).
 *
 * The signature is the cache key for the recommendation: two inputs that would
 * produce the same engine request hash equal. `selected_preferences` is sorted
 * (the engine treats it as an unordered set), while `top_priorities` order is
 * preserved (the engine treats priority order as meaningful). Every engine
 * input is included so any changed preference yields a different signature —
 * this is load-bearing for PDS-disclosure compliance: a changed input must
 * invalidate the cache and force a fresh recommendation.
 */
export function computeInputSignature(
  input: OnboardingData | RecommendationRequestV4,
): string {
  const request = toV4Request(input);
  return JSON.stringify({
    risk_category: request.risk_category ?? '',
    // Sorted copy — order-independent set semantics.
    selected_preferences: [...request.selected_preferences].sort(),
    // Order preserved — priority order is meaningful to the engine.
    top_priorities: request.top_priorities,
    rewards_preference: request.rewards_preference,
  });
}

@Injectable()
export class RecommendedFundService {
  recommendedFundList: FundProvidersData[] = [];
  imageLinks: { [key: string]: FundImages } = {};
  qualityFallback = false;
  rewardsFallback = false;
  weights: Record<string, number> = {};
  private _images: HTMLImageElement[] = [];

  /**
   * Signature of the engine inputs that produced the currently-cached
   * recommendation ({@link recommendedFundList}), or `null` when nothing has
   * been cached yet. Set only after a successful {@link preCacheFunds} fetch and
   * compared in {@link hasFreshCacheFor} to decide whether the cache can be
   * reused for a new set of inputs.
   */
  private cachedSignature: string | null = null;

  /**
   * The currently-running {@link preCacheFunds} call, keyed by the input
   * signature it is fetching, or `null` when no fetch is in flight.
   *
   * De-duplicates concurrent precaches for the SAME inputs: on the fast-nav
   * path the profile screen's fire-and-forget prefetch can still be running
   * when the reveal screen's `onInitHandler` calls `preCacheFunds`. Because
   * {@link cachedSignature} is only set at the END of a fetch, the second
   * caller would otherwise see no fresh cache and fire a SECOND concurrent
   * engine call + image preload for identical inputs. Instead, the second
   * caller awaits the existing in-flight promise. Cleared in a `finally` so a
   * later call (after completion) re-fetches normally.
   */
  private inFlight: { signature: string; promise: Promise<void> } | null = null;

  /**
   * The user's previous (current) KiwiSaver fund, resolved from the
   * `Onboarding.previous_fund_id` captured via `?previousFundID` at onboarding
   * entry — or `null` when none applies (see {@link resolvePreviousFund}).
   *
   * When set, the comparison surfaces (reveal tease, recommended-fund screen,
   * fund-details modal) compare the recommended fund against this fund instead
   * of the peer-group average. Populated by {@link preCacheFunds}; read by the
   * reveal / recommended-fund mediators, which feed it through
   * `selectComparisonBaseline()`.
   */
  private readonly _previousFund = signal<FundProvidersData | null>(null);
  readonly previousFund = this._previousFund.asReadonly();

  private readonly storageService = inject(StorageService);
  private readonly functionsService = inject(FunctionsService);
  private readonly fundProvidersService = inject(FundProvidersService);

  /**
   * Returns `true` when the cache already holds a recommendation computed from
   * the exact same engine inputs as `onboardingData` — i.e. the cache is
   * non-empty AND its stored signature matches the current inputs.
   *
   * Callers use this to decide whether {@link preCacheFunds} can be skipped.
   * A `false` result (empty cache OR changed inputs) means a fresh fetch is
   * required; the changed-inputs case is what preserves PDS-disclosure
   * compliance after a back-navigation preference change.
   */
  hasFreshCacheFor(onboardingData: OnboardingData): boolean {
    return (
      this.recommendedFundList.length > 0 &&
      this.cachedSignature === computeInputSignature(onboardingData)
    );
  }

  /**
   * Pre-cache funds using preference-based scoring (recommendation_engine_v4).
   *
   * When the cache is already fresh for `onboardingData` (same engine inputs,
   * non-empty list — see {@link hasFreshCacheFor}) this is a no-op: the existing
   * cache is reused and NOT cleared, avoiding a redundant engine call + image
   * preload. On a signature miss or empty cache it behaves as before: clear,
   * fetch, preload images, resolve the previous fund, then store the new
   * input signature.
   *
   * Concurrent calls for the SAME inputs are de-duplicated: the first call
   * starts the fetch and stores it as {@link inFlight}; subsequent callers with
   * a matching signature await that single promise rather than firing a second
   * engine call. Concurrent calls for DIFFERENT inputs are not collapsed.
   */
  async preCacheFunds(onboardingData: OnboardingData): Promise<void> {
    // Fresh-cache hit always wins first — a completed identical fetch never
    // re-runs, even past an in-flight check.
    if (this.hasFreshCacheFor(onboardingData)) {
      return;
    }

    // In-flight de-duplication: if an identical fetch is already running, await
    // it instead of starting a second concurrent engine call + image preload.
    const sig = computeInputSignature(onboardingData);
    if (this.inFlight && this.inFlight.signature === sig) {
      return this.inFlight.promise;
    }

    const promise = this._runPreCacheFunds(onboardingData).finally(() => {
      this.inFlight = null;
    });
    this.inFlight = { signature: sig, promise };
    return promise;
  }

  private async _runPreCacheFunds(onboardingData: OnboardingData): Promise<void> {
    this.recommendedFundList = [];
    this.qualityFallback = false;
    this.rewardsFallback = false;
    this.weights = {};
    this._previousFund.set(null);
    this.cachedSignature = null;

    this.recommendedFundList = await this.getRecommendationV4(onboardingData);

    // Resolve the user's previous fund (when `?previousFundID` was captured)
    // concurrently with the image fetch below — both only need the
    // recommendation result. A failed lookup falls back to the peer-group
    // average and never breaks the flow.
    const previousFundPromise = this.resolvePreviousFund(onboardingData.previous_fund_id);

    // Fetch all image URLs in parallel (3 per fund, all at once)
    await Promise.all(
      this.recommendedFundList.map(async (fund) => {
        const [titleImagePath, imageGraphPath, imageFeesPath] = await Promise.all([
          this.getImageLink(fund.imageTitle),
          this.getImageLink(fund.imageGraph),
          this.getImageLink(fund.imageFees),
        ]);
        this.imageLinks[fund.id] = { titleImagePath, imageGraphPath, imageFeesPath };
      }),
    );

    for (const fundImages of Object.values(this.imageLinks)) {
      this._preloadImage(fundImages.titleImagePath);
      this._preloadImage(fundImages.imageGraphPath);
      this._preloadImage(fundImages.imageFeesPath);
    }

    this._previousFund.set(await previousFundPromise);

    // Record the signature only for a non-empty result so a failed/empty fetch
    // (e.g. engine error returning []) is never treated as a fresh cache and a
    // later call re-fetches. Mirrors the non-empty requirement in
    // hasFreshCacheFor().
    this.cachedSignature =
      this.recommendedFundList.length > 0 ? computeInputSignature(onboardingData) : null;
  }

  private _preloadImage(path: string): void {
    if (path === '') {
      return;
    }

    // get the browser to load the image for us
    const img = new Image();
    img.src = path;
    this._images.push(img);
  }

  async getImageLink(filename: string): Promise<string> {
    let linkPath = '';

    try {
      linkPath = await this.storageService.getImageLink(filename);
    } catch (e) {
      // There will be errors but we dont mind
    }

    return linkPath;
  }

  /**
   * V4: Calls the recommendation engine with preference-based scoring.
   * No sustainability boolean — sustainability is a scored preference.
   *
   * Accepts either an `OnboardingData` record (onboarding flow) or an explicit
   * `RecommendationRequestV4` (admin / CRM preview callers).
   */
  async getRecommendationV4(
    input: OnboardingData | RecommendationRequestV4,
  ): Promise<FundProvidersData[]> {
    const callable = this.functionsService.callable<
      RecommendationRequestV4,
      RecommendationResponseV4
    >('recommendation_engine_v4');

    const request: RecommendationRequestV4 = toV4Request(input);

    try {
      const result = await callable(request);

      this.qualityFallback = result.data.quality_fallback ?? false;
      this.rewardsFallback = result.data.rewards_fallback ?? false;
      this.weights = result.data.weights ?? {};

      return result.data.fundProvidersData ?? [];
    } catch (e) {
      Sentry.captureException(`Error getting V4 recommendation ${e}`);
      return [];
    }
  }

  /**
   * Resolves the user's previous (current) fund from the `previous_fund_id`
   * captured via `?previousFundID` at onboarding entry.
   *
   * `previous_fund_id` is a `FundProviders` document ID, so it resolves to the
   * same `FundProvidersData` shape as a recommended fund. Returns `null` — so
   * every comparison surface falls back to the peer-group average — when:
   *   - no `previous_fund_id` was captured;
   *   - the ID does not resolve to a fund (not found, or any read failure);
   *   - the resolved fund is not `active` (archived / pending);
   *   - the resolved fund IS the recommended fund — a fund-vs-itself
   *     comparison is meaningless.
   *
   * A read failure never propagates: a broken previous-fund lookup must not
   * break the recommendation flow.
   */
  private async resolvePreviousFund(
    previousFundId: string | null | undefined,
  ): Promise<FundProvidersData | null> {
    if (!previousFundId) {
      return null;
    }

    let fund: FundProvidersData;
    try {
      fund = await this.fundProvidersService.getOne(previousFundId);
    } catch (e) {
      if (!(e instanceof NotFoundException)) {
        Sentry.captureException(`Error resolving previous fund ${e}`);
      }
      return null;
    }

    if (fund.state !== FundProvidersState.active) {
      return null;
    }
    if (fund.id === this.recommendedFundList[0]?.id) {
      return null;
    }
    return fund;
  }
}
